/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  Box,
  CircularProgress,
  Divider,
  Stack,
  Button,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ProductInventory } from './inventory.types';
import { API_URL, UPLOAD_URL } from '../../config/config';
import { Edit, MoveUp } from '@mui/icons-material';
import InventoryModal from './InventoryModal';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SellProductInformationModal from './SellProductInformationModal';

const InventoryById: React.FC = () => {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [inventory, setInventory] = useState<ProductInventory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isWarehouseInventory, setIsWarehouseInventory] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);

  useEffect(() => {
    const fetchInventory = async () => {
      if (!user || !id) return;

      try {
        setLoading(true);
        setError(null);

        let res = await fetch(
          `${API_URL}/inventory/${user.id}/${id}/warehouse`,
        );

        if (res.ok) {
          const data = await res.json();

          // warehouse inventory exists
          setInventory(data.data);
          setIsWarehouseInventory(true);
          return;
        }

        res = await fetch(`${API_URL}/inventory/${user.id}/${id}/store`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || 'Inventory not found');
        }

        const data = await res.json();

        setInventory(data.data);
        setIsWarehouseInventory(false);
      } catch (err: any) {
        setError(err.message || 'Unexpected error');
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, [user, id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        {error}
      </Alert>
    );
  }

  if (!inventory) {
    return (
      <Alert severity="info" sx={{ mt: 4 }}>
        No Inventories found.
      </Alert>
    );
  }

  const goToUpdateInventory = (id: string) => {
    navigate('/update-inventory/' + id);
  };

  return (
    <Box sx={{ maxWidth: '900px', mx: 'auto', mt: 4 }}>
      <Card
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          p: 3,
          borderRadius: 3,
          boxShadow: 4,
          backgroundColor: '#fafafa',
        }}
      >
        {/* Left side info */}
        <CardContent sx={{ flex: 1, pr: { md: 4 } }}>
          <Typography
            variant="h5"
            fontWeight="bold"
            gutterBottom
            color="primary"
          >
            Batch: {inventory.name}
          </Typography>
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Typography variant="body1">
              📍 Location:{' '}
              <strong>{inventory.location_in_store || 'N/A'}</strong>
            </Typography>
            <Typography variant="body1">
              💶 Price: <strong>€{inventory.product.default_price}</strong>
            </Typography>
            <Typography variant="body1">
              📦 Quantity: <strong>{inventory.quantity}</strong>
            </Typography>
            <Typography variant="body1">
              🔄 Reorder Level: <strong>{inventory.reorder_level}</strong>
            </Typography>
            <Typography variant="body1">
              ✅ Status:{' '}
              <strong style={{ textTransform: 'capitalize' }}>
                {inventory.status}
              </strong>
            </Typography>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold">
            🏬 Store Info
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {inventory.name ?? inventory.warehouse?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {inventory.store?.id_BusinessLocation?.address ??
              inventory.warehouse?.id_BusinessLocation?.address}
          </Typography>
          {isWarehouseInventory ? (
            <Box>
              <div style={{ display: 'flex' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Edit />}
                  onClick={() => goToUpdateInventory(inventory.id)}
                  style={{ marginTop: '30px', marginRight: '20px' }}
                >
                  Update
                </Button>
                <br />
                <br />
                <InventoryModal data={inventory} />
              </div>
            </Box>
          ) : (
            ''
          )}
        </CardContent>

        {/* Right side chips */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'flex-start', md: 'flex-end' },
            justifyContent: 'center',
            gap: 1,
            mt: { xs: 2, md: 0 },
          }}
        >
          <Chip
            label={`Quantity: ${inventory.quantity}`}
            color={
              inventory.quantity <= inventory.reorder_level
                ? 'error'
                : 'primary'
            }
            size="medium"
          />
          <Chip
            label={`Reorder @ ${inventory.reorder_level}`}
            variant="outlined"
            size="medium"
          />
          <Button
            variant="contained"
            color="success"
            startIcon={<MonetizationOnIcon />}
            onClick={handleOpen}
          >
            Sell Product
            <SellProductInformationModal
              modalState={open}
              setModalState={setOpen}
              product={inventory.product}
              inventoryId={inventory.id}
            />
          </Button>
        </Box>
      </Card>
      {/* Product details */}
      <Box
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 2,
          bgcolor: '#f5f5f5',
          boxShadow: 1,
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          fontWeight="bold"
          sx={{ color: 'primary.main' }}
        >
          🛒 Product Details
        </Typography>

        {inventory.product ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Typography variant="body1">
              <strong>Name:</strong> {inventory.product.name}
            </Typography>

            {inventory.product.sku && (
              <Typography variant="body1">
                <strong>SKU:</strong> {inventory.product.sku}
              </Typography>
            )}

            {inventory.product.barcode && (
              <Typography variant="body1">
                <strong>Barcode:</strong> {inventory.product.barcode}
              </Typography>
            )}

            {inventory.product.category && (
              <Typography variant="body1">
                <strong>Category:</strong> {inventory.product.category}
              </Typography>
            )}

            {inventory.product.brand && (
              <Typography variant="body1">
                <strong>Brand:</strong> {inventory.product.brand}
              </Typography>
            )}

            {inventory.product.description && (
              <Typography variant="body2" color="text.secondary">
                {inventory.product.description}
              </Typography>
            )}

            {typeof inventory.product.default_price === 'number' && (
              <Typography variant="body1">
                <strong>Default Price:</strong> €
                {inventory.product.default_price}
              </Typography>
            )}

            {typeof inventory.product.default_cost === 'number' && (
              <Typography variant="body1">
                <strong>Default Cost:</strong> €{inventory.product.default_cost}
              </Typography>
            )}

            {inventory.product.image_url && (
              <Box
                component="img"
                src={inventory.product.image_url}
                alt={inventory.product.name}
                sx={{
                  mt: 2,
                  maxHeight: 200,
                  borderRadius: 1,
                  objectFit: 'cover',
                  boxShadow: 1,
                }}
              />
            )}

            {/* Product Images */}
            {Array.isArray(inventory.product.ProductImages) &&
              inventory.product.ProductImages.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    Product Images
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    {inventory.product.ProductImages.map(img => (
                      <Box
                        key={img.id}
                        component="img"
                        src={img.url}
                        alt={img.alt_text || 'Product image'}
                        sx={{
                          width: '100%', // full width of the container
                          maxWidth: 400, // or increase to 500/600 if you want it larger
                          height: 'auto',
                          borderRadius: 2,
                          objectFit: 'cover',
                          boxShadow: 3,
                          mx: 'auto', // centers the image horizontally
                        }}
                      />
                    ))}
                  </Stack>
                </>
              )}
            {/* Product Variants */}
            {Array.isArray(inventory.product.ProductVariants) &&
              inventory.product.ProductVariants.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    Product Variants
                  </Typography>
                  <Stack spacing={1}>
                    {inventory.product.ProductVariants.map(variant => (
                      <Box key={variant.id}>
                        {variant.name && (
                          <Typography variant="body2">
                            <strong>Name</strong> {variant.name}
                          </Typography>
                        )}
                        {variant.size && (
                          <Typography variant="body2">
                            <strong>Size:</strong> {variant.size}
                          </Typography>
                        )}
                        {variant.color && (
                          <Typography variant="body2">
                            <strong>Color:</strong> {variant.color}
                          </Typography>
                        )}
                        {variant.material && (
                          <Typography variant="body2">
                            <strong>Material:</strong> {variant.material}
                          </Typography>
                        )}
                        {variant.sku && (
                          <Typography variant="body2">
                            <strong>SKU:</strong> {variant.sku}
                          </Typography>
                        )}
                        {variant.barcode && (
                          <Typography variant="body2">
                            <strong>Barcode:</strong> {variant.barcode}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </>
              )}

            <Typography variant="caption" color="text.secondary">
              Created at:{' '}
              {new Date(inventory.product.created_at).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Updated at:{' '}
              {new Date(inventory.product.updated_at).toLocaleString()}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ mt: 1 }} color="error">
            ⚠️ No product data available for this inventory.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default InventoryById;
